<template>
  <div class="page-content">
    <breadcrumb-index 
      :items="breadcrumbs"
      icon="layers"
      :badge="topics.length ? `${topics.length} topics` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button v-if="canModify" @click="openCreateModal" class="btn btn-primary btn-sm">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Topic
        </button>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="filteredTopics"
      :columns="columns"
      :loading="loading"
      search-placeholder="Search topics..."
      empty-icon="layers"
      empty-title="No topics found"
      empty-description="Add your first topic to get started"
    >
      <template #cell-order="{ item, value }">
        <span class="order-badge">{{ item.order || value }}</span>
      </template>

      <template #cell-topic="{ item }">
        <router-link :to="`/app/topics/${item.id}/lessons`" class="avatar-name text-primary">
          {{ item.name }}
        </router-link>
      </template>

      <template #cell-lessons="{ item }">
        <router-link :to="`/app/topics/${item.id}/lessons`" class="badge bg-info text-decoration-none">
          {{ item.lessonCount || 0 }} lessons
        </router-link>
      </template>

      <template #cell-notes="{ item }">
        <router-link :to="`/app/topics/${item.id}/notes`" class="badge bg-warning text-decoration-none">
          {{ item.notesCount || 0 }} notes
        </router-link>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', item.status === 'active' ? 'status-active' : 'status-inactive']">
          {{ item.status }}
        </span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <router-link 
            :to="`/app/topics/${item.id}/lessons`" 
            class="action-btn action-view" 
            v-tooltip="'View lessons'"
          >
            <vue-feather type="book-open" size="14" />
          </router-link>
          <router-link 
            :to="`/app/topics/${item.id}/notes`" 
            class="action-btn" 
            v-tooltip="{ text: 'View notes', variant: 'warning' }"
            style="color: #f59e0b;"
          >
            <vue-feather type="file-text" size="14" />
          </router-link>
          <button 
            v-if="canModify"
            class="action-btn action-edit" 
            @click="openEditModal(item)"
            v-tooltip="'Edit topic'"
          >
            <vue-feather type="edit-2" size="14" />
          </button>
          <button 
            v-if="canModify"
            class="action-btn action-delete" 
            @click="confirmDelete(item)" 
            v-tooltip="{ text: 'Delete topic', variant: 'danger' }"
          >
            <vue-feather type="trash-2" size="14" />
          </button>
        </div>
      </template>

      <template #empty-action>
        <button @click="openCreateModal" class="btn btn-primary">
          <vue-feather type="plus" size="16" class="me-1" />
          Add First Topic
        </button>
      </template>
    </DataTable>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Topic' : 'Add Topic'"
      icon="layers"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.name"
      :item-id="isEditing ? editingId : ''"
      @submit="saveTopic"
    >
      <form @submit.prevent="saveTopic" class="modal-form">
        <div class="form-group">
          <label class="form-label">Topic Name <span class="text-danger">*</span></label>
          <input 
            v-model="form.name" 
            type="text" 
            class="form-control" 
            placeholder="e.g., Algebra, Thermodynamics"
            required
          />
        </div>

        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea 
            v-model="form.description" 
            class="form-control" 
            rows="3"
            placeholder="Brief description of the topic..."
          ></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Order</label>
            <input 
              v-model.number="form.order" 
              type="number" 
              class="form-control" 
              placeholder="1"
              min="1"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Status</label>
            <select v-model="form.status" class="form-control">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
          </div>
        </div>

        <!-- AI Translate Button -->
        <div class="ai-enhance-section" v-if="form.name">
          <button type="button" @click="aiTranslateTopic" class="ai-translate-btn" :disabled="aiTranslating">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'AI Translate' }}
          </button>
        </div>

        <!-- Translation Language Selector -->
        <div class="ai-translate-section" v-if="form.name && showTranslateOptions">
          <label class="form-label">Translate to</label>
          <select v-model="translateTargetLang" class="form-control form-control-sm" style="max-width: 200px;">
            <option v-for="lang in languageOptions.filter(l => l.code !== form.language)" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <button type="button" @click="confirmTranslateTopic" class="ai-translate-btn" :disabled="aiTranslating || !translateTargetLang">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'Translate Now' }}
          </button>
        </div>

        <div class="form-group">
          <label class="form-label">Language</label>
          <select v-model="form.language" class="form-control" @change="onLanguageChange">
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <small v-if="loadingTranslation" class="text-info"><vue-feather type="loader" size="12" class="spin" /> Loading translation...</small>
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useAlert } from '@/plugins/sweetalert'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'
import { languageOptions } from '@/utils/languageOptions'

export default {
  name: 'TopicsList',
  components: { DataTable, CreateModal },
  setup() {
    const route = useRoute()
    const authStore = useAuthStore()
    const alert = useAlert()
    const subjectId = route.params.subjectId
    const canModify = computed(() => authStore.isSuperAdmin || authStore.isAdmin || authStore.isContentManager)

    const columns = [
      { key: 'order', label: '#', width: '60px' },
      { key: 'topic', label: 'Topic Name', sortable: true, minWidth: '250px' },
      { key: 'lessons', label: 'Lessons', class: 'text-center' },
      { key: 'notes', label: 'Notes', class: 'text-center' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const subject = ref(null)
    const topics = ref([])
    const loading = ref(true)

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const form = reactive({
      name: '',
      description: '',
      order: 1,
      status: 'draft',
      language: 'en'
    })

    // Translation state
    const aiTranslating = ref(false)
    const showTranslateOptions = ref(false)
    const translateTargetLang = ref('hi')
    const loadingTranslation = ref(false)

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      if (subject.value?.examId) {
        items.push({ title: subject.value?.examName || 'Exam', url: `/app/exams/${subject.value.examId}/subjects` })
      }
      items.push({ title: subject.value?.name || 'Subject', active: true })
      return items
    })

    const filteredTopics = computed(() => topics.value)

    const fetchData = async () => {
      loading.value = true
      try {
        const [subjectRes, topicsRes] = await Promise.all([
          api.get(`/v1/admin/subjects/${subjectId}`),
          api.get(`/v1/admin/subjects/${subjectId}/topics`)
        ])
        subject.value = subjectRes.data.subject
        topics.value = topicsRes.data.topics || []
      } catch (err) {
        console.error('Failed to fetch data:', err)
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      form.name = ''
      form.description = ''
      form.order = topics.value.length + 1
      form.status = 'draft'
      form.language = 'en'
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openEditModal = (topic) => {
      isEditing.value = true
      editingId.value = topic.id
      form.name = topic.name || ''
      form.description = topic.description || ''
      form.order = topic.order || 1
      form.status = topic.status || 'draft'
      form.language = topic.language || 'en'
      showModal.value = true
    }

    const saveTopic = async () => {
      if (!form.name) return
      
      saving.value = true
      try {
        const payload = {
          name: form.name,
          description: form.description,
          order: form.order,
          status: form.status,
          language_code: form.language
        }

        if (isEditing.value) {
          const response = await api.put(`/v1/admin/topics/${editingId.value}`, payload)
          const index = topics.value.findIndex(t => t.id === editingId.value)
          if (index !== -1) {
            topics.value[index] = { ...topics.value[index], ...response.data.topic || payload }
          }
          alert.success('Updated!', 'Topic updated successfully')
        } else {
          const response = await api.post(`/v1/admin/subjects/${subjectId}/topics`, payload)
          topics.value.push(response.data.topic || response.data)
          alert.success('Created!', 'Topic created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        alert.error('Error', error.response?.data?.error?.message || 'Failed to save topic')
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = async (topic) => {
      const result = await alert.delete(
        'Delete Topic?',
        `This will also delete all lessons and notes under "${topic.name}".`
      )

      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/topics/${topic.id}`)
          topics.value = topics.value.filter(t => t.id !== topic.id)
          alert.success('Deleted!', 'Topic has been deleted.')
        } catch (err) {
          alert.error('Error', 'Failed to delete topic')
        }
      }
    }

    onMounted(fetchData)

    // AI Translation
    const aiTranslateTopic = () => {
      if (!form.name) {
        alert.error('Error', 'Please enter a topic name first')
        return
      }
      showTranslateOptions.value = !showTranslateOptions.value
    }

    const confirmTranslateTopic = async () => {
      if (!form.name || !translateTargetLang.value) return
      const targetLangLabel = languageOptions.find(l => l.code === translateTargetLang.value)?.label || translateTargetLang.value
      aiTranslating.value = true
      try {
        const fieldsToTranslate = { name: form.name }
        if (form.description) fieldsToTranslate.description = form.description

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Translate the following educational topic content to ${targetLangLabel}. Keep technical terms intact. Return ONLY a valid JSON object with the same keys translated:\n\n${JSON.stringify(fieldsToTranslate, null, 2)}`,
          generation_type: 'translate',
          output_format: 'structured'
        })
        let aiData = response.data?.structured_data?.data || {}
        if (!aiData.name) {
          const responseText = response.data?.response || response.data?.message || ''
          const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
          if (jsonMatch) { try { aiData = JSON.parse(jsonMatch[1].trim()) } catch (e) {} }
          if (!aiData.name) { try { aiData = JSON.parse(responseText) } catch (e) {} }
        }
        let fieldsUpdated = 0
        if (aiData.name) { form.name = aiData.name; fieldsUpdated++ }
        if (aiData.description) { form.description = aiData.description; fieldsUpdated++ }
        if (fieldsUpdated > 0) {
          form.language = translateTargetLang.value
          alert.success('Translated!', `${fieldsUpdated} field(s) translated to ${targetLangLabel}`)
        } else {
          alert.warning('Partial Success', 'AI responded but could not extract translated data.')
        }
        showTranslateOptions.value = false
      } catch (error) {
        console.error('AI translation failed:', error)
        alert.error('Error', 'Failed to translate topic with AI.')
      } finally {
        aiTranslating.value = false
      }
    }

    // Fetch translation when language changes in edit mode
    const onLanguageChange = async () => {
      if (!isEditing.value || !editingId.value) return
      loadingTranslation.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'topic', content_id: editingId.value, language_code: form.language }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }
          if (td.name) form.name = td.name
          if (td.description) form.description = td.description
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        loadingTranslation.value = false
      }
    }

    return { 
      subjectId, 
      columns,
      subject, 
      topics, 
      loading,
      breadcrumbs, 
      filteredTopics,
      showModal,
      isEditing,
      saving,
      editingId,
      form,
      canModify,
      aiTranslating, showTranslateOptions, translateTargetLang,
      loadingTranslation, onLanguageChange,
      openCreateModal,
      openEditModal,
      saveTopic,
      confirmDelete,
      aiTranslateTopic, confirmTranslateTopic,
      languageOptions
    }
  }
}
</script>

<style scoped>
.order-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f1f5f9;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  color: #64748b;
}

:global(body.dark-mode) .order-badge {
  background: #334155;
  color: #94a3b8;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

.ai-enhance-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  border: 1px solid #fbbf24;
  flex-wrap: wrap;
}

.ai-translate-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-translate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.ai-translate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
  flex-wrap: wrap;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
